import { Apis } from '@/appSettings'
import { DashboardHeader } from '@/containers/dashboard/DashboardHeader'
import { usePartner } from '@/context/PartnerContext'
import { useInsurers, useUrl } from '@/hooks'
import { useCreateStaticFile, useGetStaticFiles, useUpdateStaticFile } from '@/hooks/useDocuments'
import { Upload } from '@/icons'
import { PageWrapper } from '@/layouts/components/PageWrapper'
import { Box, Button, Link as ChakraLink, HStack, Icon, IconButton, VisuallyHidden } from '@chakra-ui/react'
import { faDownload, faTrash } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateFormControl, InputFormControl, SelectFormControl } from 'BrokerTsLib/src/components'
import { EditableGrid, type EditableGridColumnDef, type EditableGridRow } from 'BrokerTsLib/src/containers/EditableGrid'
import type { StaticFileDto } from 'BrokerTsLib/src/dtos'
import { StaticFileType } from 'BrokerTsLib/src/enums/StaticFileType'
import { useMemo } from 'react'
import * as yup from 'yup'

const stripExtension = (fileName: string | null | undefined): string | null => {
  if (!fileName) return null
  const lastDot = fileName.lastIndexOf('.')
  return lastDot > 0 ? fileName.substring(0, lastDot) : fileName
}

const normalizeRow = (row: EditableGridRow<StaticFileDto>): EditableGridRow<StaticFileDto> => ({
  ...row,
  name: (row.name ?? '').trim(),
  code: (row.code ?? '').trim(),
  fileName: stripExtension(row.fileName),
})

const schema = yup.object().shape({
  id: yup.string().optional(),
  name: yup.string().required('Název je povinný'),
  type: yup.number().required('Typ je povinný'),
  insurerId: yup.number().nullable().optional(),
  code: yup.string().nullable().optional(),
  validFrom: yup.string().nullable().optional(),
  validTo: yup.string().nullable().optional(),
}) as any

export const StaticFilesPage = () => {
  const { data: files, isLoading, refetch } = useGetStaticFiles()
  const { mutateAsync: createStaticFile } = useCreateStaticFile()
  const { mutateAsync: updateStaticFile } = useUpdateStaticFile()

  const { data: insurersData, isLoading: isLoadingInsurers } = useInsurers(null, null)
  const insurers = useMemo(() => insurersData?.data ?? [], [insurersData])
  const { getBaseUrl } = usePartner()

  const newRowFactory = () => ({
    id: '',
    type: StaticFileType.Vpp,
    name: '',
    fileName: null,
    insurerId: null,
    insurerName: null,
    code: null,
    validFrom: null,
    validTo: null,
    file: null,
  } as unknown as StaticFileDto)

  const onCommitChanges = async (created: StaticFileDto[], edited: StaticFileDto[], deletedIds: string[]) => {
    // Pro každý nově vytvořený
    for (const item of created) {
      await createStaticFile(item)
    }

    // Pro editované (metadata nebo byl nastaven nový soubor pro aktualizaci)
    for (const item of edited) {
      await updateStaticFile(item)
    }

    // Soft delete zneplatněním dokumentu pro vymazané přes Grid tlačítko Remove
    // Note: Grid vrací IDčka, takže si najdeme původní entitu.
    const deletedFiles = files?.filter(f => deletedIds.includes(f.id)) ?? []
    for (const item of deletedFiles) {
      if (!item.validTo) { // pokud náhodou nebylo už zneplatněno
        await updateStaticFile({ ...item, validTo: new Date().toISOString() })
      }
    }

    await refetch()
  }

  const columns = useMemo<EditableGridColumnDef<StaticFileDto>[]>(() => [
    {
      header: 'Kód',
      accessorKey: 'code',
      cell: ({ row, control }) => (
        <InputFormControl
          name={`root.${row.index}.code`}
          control={control}
          variant="grid"
        />
      ),
    },
    {
      header: 'Název',
      accessorKey: 'name',
      cell: ({ row, control }) => (
        <InputFormControl
          name={`root.${row.index}.name`}
          control={control}
          variant="grid"
        />
      ),
    },
    {
      header: 'Název souboru',
      accessorKey: 'fileName',
      cell: ({ row, control }) => (
        <InputFormControl
          name={`root.${row.index}.fileName`}
          control={control}
          variant="grid"
        />
      ),
    },
    {
      header: 'Typ',
      accessorKey: 'type',
      cell: ({ row, control }) => (
        <SelectFormControl
          name={`root.${row.index}.type`}
          control={control}
          variant="grid"
          options={[
            { value: StaticFileType.Vpp, label: 'VPP' }
          ]}
          valueAsNumber
          unselectedText="--"
        />
      ),
    },
    {
      header: 'Pojišťovna',
      accessorKey: 'insurerId',
      cell: ({ row, control }) => (
        <SelectFormControl
          name={`root.${row.index}.insurerId`}
          control={control}
          variant="grid"
          options={insurers.map((i: any) => ({ value: i.id, label: i.name }))}
          valueAsNumber
          unselectedText="--"
          isLoading={isLoadingInsurers}
        />
      ),
    },
    {
      header: 'Platnost od',
      accessorKey: 'validFrom',
      cell: ({ row, control }) => (
        <DateFormControl
          name={`root.${row.index}.validFrom`}
          control={control}
          enableTime
          variant="grid"
        />
      ),
    },
    {
      header: 'Platnost do',
      accessorKey: 'validTo',
      cell: ({ row, control }) => (
        <DateFormControl
          name={`root.${row.index}.validTo`}
          control={control}
          enableTime
          variant="grid"
        />
      ),
    },
    {
      header: 'Soubor',
      id: '_actions',
      cell: ({ row, removeRow, updateRow, control }) => {
        const fileId = row.original.id

        return (
          <HStack ml="auto" justifyContent="right" gap={2}>
            {/* Download Link */}
            {fileId && (
              <Button
                as={ChakraLink}
                href={`${getBaseUrl()}/Documents/${fileId}`}
                target="_blank"
                size="sm"
                variant="ghost"
                colorScheme="primary"
                leftIcon={<Icon as={FontAwesomeIcon} icon={faDownload} />}
              >
                Stáhnout
              </Button>
            )}

            {/* Upload nového souboru (pro nový i update) */}
            <Box position="relative">
              <VisuallyHidden>
                <input
                  id={`fileInput_${row.id}`}
                  type="file"
                  onChange={(e) => {
                    const selectedFile = e.target.files?.[0] || null
                    if (selectedFile) {
                      const update: Partial<StaticFileDto> = { file: selectedFile }
                      if (!row.original.fileName) {
                        update.fileName = stripExtension(selectedFile.name)
                      }
                      updateRow(row.index, update)
                    }
                  }}
                  multiple={false}
                  accept=".jpg, .png, .jpeg, .gif, .bmp, .tif, .svg, .tiff, .pdf, .docx, .doc, application/pdf|image/*"
                />
              </VisuallyHidden>
              <Button
                type="button"
                variant={row.original.file ? 'solid' : 'outline'}
                colorScheme={row.original.file ? 'green' : 'gray'}
                size="sm"
                onClick={() => document.getElementById(`fileInput_${row.id}`)?.click()}
                title={row.original.file ? row.original.file.name : 'Nahrát soubor'}
              >
                <Upload color={row.original.file ? 'white' : 'secondary.500'} />
                {row.original.file ? ' Vybráno' : (fileId ? '' : ' Vybrat')}
              </Button>
            </Box>

            {/* Invalidate / Smazat - volá se standardní Grid Delete action,
                kterou si v onCommitChanges překládáme na nastavení validTo aktuálním datem (soft-delete)
            */}
            {(!fileId || !row.original.validTo) && (
              <IconButton
                aria-label="Delete"
                variant="ghost"
                size="sm"
                colorScheme="red"
                icon={<Icon as={FontAwesomeIcon} icon={faTrash} />}
                onClick={() => removeRow(row.index)}
                title={fileId ? 'Zneplatnit' : 'Odebrat'}
              />
            )}
          </HStack>
        )
      },
    },
  ], [insurers, isLoadingInsurers])

  return (
    <PageWrapper
      header={<DashboardHeader title="Správa dokumentů" />}
      mainProps={{
        sx: {
          pt: { base: 0, md: 8 },
          gap: 6,
          display: 'grid',
        },
      }}
    >
      <Box bg="white" p={6} borderRadius="lg" shadow="sm">
        <EditableGrid
          defaultValues={files}
          isLoading={isLoading}
          columnDefs={columns}
          newRowFactory={newRowFactory}
          normalizeRow={normalizeRow}
          schema={schema}
          onCommitChanges={onCommitChanges}
        />
      </Box>
    </PageWrapper>
  )
}
